<template>
  <div>
    <nav class="navbar navbar-top navbar-expand-md bg-white shadow-sm border-bottom" id="navbar-main">
      <div class="container-fluid">
        <div>
          <p class="text-uppercase text-muted mb-1 super-page-kicker">Vilogi</p>
          <h2 class="h3 mb-0">Email Mismatches</h2>
        </div>
        <user-data></user-data>
      </div>
    </nav>

    <div class="container-fluid mt-0 pt-5 pt-md-8">

      <!-- Header card -->
      <div class="card shadow-sm border-0 mb-4">
        <div class="card-body py-4">
          <div class="row align-items-center">
            <div class="col-lg-8">
              <p class="super-page-kicker mb-2">Vilogi Sync</p>
              <h3 class="mb-1">Owner email mismatches</h3>
              <p class="text-muted mb-0">
                Vilogi has a different email on file than SnapNBill for these lot owners, found during the
                daily adherent sync. Ignore a mismatch to stop seeing it, or update the owner's email in
                Manage Address Book and mark it resolved here.
              </p>
            </div>
            <div class="col-lg-4 text-lg-right mt-3 mt-lg-0">
              <router-link :to="{ name: 'manageAddressBook' }" class="btn btn-outline-primary">
                Manage Address Book
              </router-link>
            </div>
          </div>
        </div>
      </div>

      <!-- Mismatch list -->
      <div class="card shadow-sm border-0">
        <div class="card-header bg-white border-0 py-3">
          <div class="d-flex flex-wrap" style="gap: 0.5rem;">
            <b-button
              v-for="tab in statusTabs"
              :key="tab.value || 'all'"
              size="sm"
              pill
              :variant="activeStatus === tab.value ? 'primary' : 'outline-secondary'"
              @click="selectStatus(tab.value)">
              {{ tab.label }}
            </b-button>
          </div>
        </div>

        <div v-if="loading" class="text-center py-6">
          <b-spinner variant="primary" class="mb-3"></b-spinner>
          <p class="text-muted mb-0">Loading mismatches...</p>
        </div>

        <div v-else-if="rows.length === 0" class="text-center py-6">
          <i class="fas fa-check-circle text-muted mb-3" style="font-size: 2rem;"></i>
          <h4 class="mb-1">Nothing here</h4>
          <p class="text-muted mb-0">No {{ activeTabLabel.toLowerCase() }} email mismatches.</p>
        </div>

        <div v-else class="table-responsive">
          <b-table striped hover :items="rows" :fields="tableFields" class="mb-0">
            <template slot="owner" slot-scope="data">
              <div>
                <h5 class="mb-0">{{ data.item.lot_owner ? data.item.lot_owner.name : '—' }}</h5>
                <small class="text-muted">Lot {{ data.item.lot_owner ? data.item.lot_owner.lot_no : '—' }}</small>
              </div>
            </template>

            <template slot="vilogi_email" slot-scope="data">
              <span class="text-monospace">{{ data.item.vilogi_email }}</span>
            </template>

            <template slot="snapnbill_email" slot-scope="data">
              <span class="text-monospace">{{ data.item.snapnbill_email }}</span>
            </template>

            <template slot="status" slot-scope="data">
              <b-badge :variant="statusVariant(data.item.status)" class="px-2 py-1">
                {{ statusLabel(data.item.status) }}
              </b-badge>
            </template>

            <template slot="created_at" slot-scope="data">
              {{ data.item.created_at | fmtDateTime }}
            </template>

            <template slot="actions" slot-scope="data">
              <b-button
                size="sm"
                variant="outline-secondary"
                class="mr-2"
                :disabled="data.item.status === 'ignored' || updatingId === data.item.id"
                @click="setStatus(data.item, 'ignored')">
                <b-spinner v-if="updatingId === data.item.id" small></b-spinner>
                <span v-else>Ignore</span>
              </b-button>
              <b-button
                size="sm"
                variant="outline-success"
                :disabled="data.item.status === 'updated' || updatingId === data.item.id"
                @click="setStatus(data.item, 'updated')">
                <b-spinner v-if="updatingId === data.item.id" small></b-spinner>
                <span v-else>Mark Resolved</span>
              </b-button>
            </template>
          </b-table>

          <div class="card-footer border-0">
            <b-pagination
              align="right"
              @input="pageClick"
              :total-rows="pagination.total || 0"
              v-model="currentPage"
              :per-page="pagination.per_page || 15">
            </b-pagination>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ApiServices from '@/services/api'
import userData from '@/pages/Common/userData'

export default {
  components: { userData },
  data() {
    return {
      loading: true,
      updatingId: null,
      activeStatus: 'unread',
      currentPage: 1,
      pagination: {},
      rows: [],
      statusTabs: [
        { value: 'unread', label: 'Unread' },
        { value: 'ignored', label: 'Ignored' },
        { value: 'updated', label: 'Resolved' },
        { value: null, label: 'All' }
      ],
      tableFields: [
        { key: 'owner', label: 'Owner', sortable: false },
        { key: 'vilogi_email', label: 'Vilogi Email', sortable: false },
        { key: 'snapnbill_email', label: 'SnapNBill Email', sortable: false },
        { key: 'status', label: 'Status', sortable: false, class: 'text-center' },
        { key: 'created_at', label: 'Found', sortable: false },
        { key: 'actions', label: '', class: 'text-right' }
      ]
    }
  },
  computed: {
    activeTabLabel() {
      const match = this.statusTabs.find(t => t.value === this.activeStatus)
      return match ? match.label : 'matching'
    }
  },
  methods: {
    fetchMismatches() {
      this.loading = true
      const params = { page: this.currentPage }
      if (this.activeStatus) params.status = this.activeStatus

      return ApiServices.getVilogiEmailMismatches(params)
        .then(response => {
          const paged = (response && response.data) || {}
          this.rows = paged.data || []
          this.pagination = {
            total: paged.total || 0,
            per_page: paged.per_page || 15
          }
        })
        .catch(() => {
          this.rows = []
          this.pagination = {}
        })
        .finally(() => {
          this.loading = false
        })
    },
    selectStatus(value) {
      this.activeStatus = value
      this.currentPage = 1
      this.fetchMismatches()
    },
    pageClick(page) {
      this.currentPage = page
      this.fetchMismatches()
    },
    statusVariant(status) {
      if (status === 'ignored') return 'secondary'
      if (status === 'updated') return 'success'
      return 'warning'
    },
    statusLabel(status) {
      if (status === 'ignored') return 'Ignored'
      if (status === 'updated') return 'Resolved'
      return 'Unread'
    },
    setStatus(row, status) {
      this.updatingId = row.id
      ApiServices.updateVilogiEmailMismatch(row.id, { status })
        .then(response => {
          this.SUCCESS_TOAST_MSG(response.message || 'Status updated')
          return this.fetchMismatches()
        })
        .catch(() => {})
        .finally(() => {
          this.updatingId = null
        })
    }
  },
  created() {
    this.fetchMismatches()
  }
}
</script>

<style scoped>
.super-page-kicker {
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  color: #5e72e4;
}

.py-6 {
  padding-top: 3rem;
  padding-bottom: 3rem;
}
</style>



// WEBPACK FOOTER //
// src/pages/Admin/vilogiEmailMismatches.vue